import React, { useRef, useState } from "react";
import { Box, ButtonBase, GlobalStyles, Paper, Stack, Typography } from "@mui/material";

export default function LoginShell({ eyebrow, title, subtitle, children, footer }) {
  const [lampOn, setLampOn] = useState(false);
  const [pullDistance, setPullDistance] = useState(0);
  const [dragging, setDragging] = useState(false);
  const pullStartYRef = useRef(0);
  const pullTriggeredRef = useRef(false);
  const suppressClickRef = useRef(false);

  const toggleLamp = () => {
    setLampOn(current => !current);
  };

  const startPull = (event) => {
    pullStartYRef.current = event.clientY;
    pullTriggeredRef.current = false;
    setDragging(true);
    event.currentTarget.setPointerCapture?.(event.pointerId);
  };

  const movePull = (event) => {
    if (!dragging) return;
    const distance = Math.min(Math.max(event.clientY - pullStartYRef.current, 0), 82);
    setPullDistance(distance);
    if (distance > 34) pullTriggeredRef.current = true;
  };

  const endPull = () => {
    if (dragging && pullTriggeredRef.current) {
      toggleLamp();
      suppressClickRef.current = true;
    }
    setDragging(false);
    setPullDistance(0);
  };

  const handleCordClick = () => {
    if (suppressClickRef.current) {
      suppressClickRef.current = false;
      return;
    }
    toggleLamp();
  };

  return (
    <Box
      sx={{
        minHeight: "100dvh",
        width: "100%",
        position: "relative",
        overflow: "hidden",
        display: "grid",
        placeItems: "center",
        px: { xs: 2, sm: 3 },
        py: { xs: 4, md: 6 },
        backgroundImage: lampOn
          ? "linear-gradient(120deg, rgba(2, 12, 24, 0.56), rgba(3, 48, 64, 0.24), rgba(8, 71, 72, 0.36)), url('/clipedbackground.jpg')"
          : "linear-gradient(120deg, rgba(2, 12, 24, 0.94), rgba(1, 22, 34, 0.82), rgba(2, 42, 48, 0.88)), url('/clipedbackground.jpg')",
        backgroundSize: "cover",
        backgroundPosition: "center",
        transition: "background-image 260ms ease",
      }}
    >
      <GlobalStyles
        styles={{
          "@keyframes scanLampSwing": {
            "0%, 100%": { transform: "translateX(-50%) rotate(-1.8deg)" },
            "50%": { transform: "translateX(-50%) rotate(1.8deg)" },
          },
          "@keyframes scanLampWarm": {
            "0%, 100%": { opacity: 0.54 },
            "50%": { opacity: 0.82 },
          },
          "@keyframes scanBulbPulse": {
            "0%, 100%": { boxShadow: "0 0 20px rgba(255,220,132,0.62), 0 0 54px rgba(255,190,76,0.3)" },
            "50%": { boxShadow: "0 0 28px rgba(255,230,156,0.82), 0 0 72px rgba(255,190,76,0.42)" },
          },
        }}
      />
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          background: "linear-gradient(180deg, rgba(0,0,0,0.08), rgba(0,0,0,0.38))",
        }}
      />

      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: "50%",
          zIndex: 3,
          width: { xs: 280, sm: 380 },
          height: { xs: 330, sm: 390 },
          transform: "translateX(-50%)",
          transformOrigin: "top center",
          animation: dragging ? "scanLampSwing 560ms ease-in-out infinite" : "none",
          pointerEvents: "none",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: "50%",
            width: 4,
            height: { xs: 76, sm: 92 },
            transform: "translateX(-50%)",
            background: "linear-gradient(90deg, #02080b 0%, #5d7078 46%, #0b1418 100%)",
            opacity: 0.92,
            boxShadow: "0 8px 14px rgba(0,0,0,0.34)",
          }}
        />
        <Box
          sx={{
            position: "absolute",
            top: { xs: 62, sm: 76 },
            left: "50%",
            width: { xs: 56, sm: 66 },
            height: { xs: 22, sm: 26 },
            transform: "translateX(-50%)",
            borderRadius: "50%",
            background: "linear-gradient(180deg, #34434a 0%, #0d171b 84%)",
            border: "1px solid rgba(255,255,255,0.18)",
            boxShadow: "inset 0 2px 4px rgba(255,255,255,0.16), 0 14px 22px rgba(0,0,0,0.34)",
          }}
        />
        <Box
          sx={{
            position: "absolute",
            top: { xs: 78, sm: 94 },
            left: "50%",
            width: { xs: 128, sm: 164 },
            height: { xs: 68, sm: 82 },
            transform: "translateX(-50%)",
            clipPath: "polygon(18% 0, 82% 0, 100% 100%, 0 100%)",
            background:
              "linear-gradient(90deg, rgba(255,255,255,0.12), transparent 18%, transparent 78%, rgba(0,0,0,0.28)), linear-gradient(180deg, #2f424a 0%, #111b20 42%, #26373e 100%)",
            border: "1px solid rgba(255,255,255,0.18)",
            boxShadow: lampOn
              ? "inset 0 8px 12px rgba(255,255,255,0.08), 0 18px 48px rgba(255, 210, 118, 0.32), 0 18px 34px rgba(0,0,0,0.38)"
              : "inset 0 8px 12px rgba(255,255,255,0.06), 0 18px 34px rgba(0,0,0,0.46)",
          }}
        />
        <Box
          sx={{
            position: "absolute",
            top: { xs: 100, sm: 121 },
            left: "50%",
            width: { xs: 80, sm: 104 },
            height: { xs: 44, sm: 52 },
            transform: "translateX(-50%)",
            zIndex: 2,
            pointerEvents: "none",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              px: { xs: 1.6, sm: 2.1 },
              pt: { xs: 0.9, sm: 1.1 },
            }}
          >
            {[0, 1].map(index => (
              <Box
                key={index}
                sx={{
                  width: { xs: 17, sm: 21 },
                  height: { xs: 17, sm: 21 },
                  borderRadius: "50%",
                  transition: "all 180ms ease",
                  background: lampOn
                    ? "radial-gradient(circle at 38% 32%, #ffffff 0 12%, #18232a 13% 100%)"
                    : "transparent",
                  border: lampOn ? "2px solid rgba(255,255,255,0.74)" : "0",
                  borderBottom: lampOn ? undefined : "3px solid rgba(225,236,239,0.88)",
                  transform: lampOn ? "translateY(0)" : "translateY(4px) rotate(0deg)",
                  boxShadow: lampOn
                    ? "0 0 10px rgba(255,235,169,0.36), inset 0 -2px 2px rgba(0,0,0,0.22)"
                    : "0 5px 7px rgba(0,0,0,0.28)",
                }}
              />
            ))}
          </Box>
          <Box
            sx={{
              position: "absolute",
              left: "50%",
              bottom: { xs: 4, sm: 5 },
              width: lampOn ? { xs: 34, sm: 42 } : { xs: 18, sm: 24 },
              height: lampOn ? { xs: 16, sm: 18 } : 2,
              transform: "translateX(-50%)",
              borderBottom: lampOn ? "4px solid rgba(255,238,190,0.92)" : "2px solid rgba(173,192,198,0.74)",
              borderRadius: lampOn ? "0 0 999px 999px" : 0,
              transition: "all 180ms ease",
              filter: lampOn ? "drop-shadow(0 0 7px rgba(255,225,140,0.45))" : "none",
            }}
          />
        </Box>
        <Box
          sx={{
            position: "absolute",
            top: { xs: 140, sm: 169 },
            left: "50%",
            width: { xs: 134, sm: 172 },
            height: { xs: 20, sm: 24 },
            transform: "translateX(-50%)",
            borderRadius: "50%",
            background: lampOn
              ? "radial-gradient(circle, rgba(255,239,180,0.78) 0 18%, rgba(38,55,62,0.96) 58%, #101a20 100%)"
              : "radial-gradient(circle, rgba(86,102,108,0.45) 0 20%, rgba(38,55,62,0.96) 60%, #101a20 100%)",
            border: "1px solid rgba(255,255,255,0.2)",
            boxShadow: "inset 0 -4px 8px rgba(0,0,0,0.42), 0 10px 18px rgba(0,0,0,0.28)",
          }}
        />
        <Box
          sx={{
            position: "absolute",
            top: { xs: 128, sm: 154 },
            left: "50%",
            width: { xs: 34, sm: 42 },
            height: { xs: 44, sm: 52 },
            transform: "translateX(-50%)",
            borderRadius: "46% 46% 48% 48%",
            background: lampOn
              ? "radial-gradient(circle at 42% 28%, #fff8dd 0 18%, #ffd875 42%, #f2a83a 100%)"
              : "linear-gradient(180deg, rgba(128,143,148,0.62), rgba(34,49,55,0.92))",
            border: "1px solid rgba(255,255,255,0.36)",
            opacity: lampOn ? 1 : 0.72,
            animation: lampOn ? "scanBulbPulse 2.6s ease-in-out infinite" : "none",
            boxShadow: lampOn
              ? "0 0 24px rgba(255,220,132,0.72), 0 0 64px rgba(255,190,76,0.32)"
              : "inset 0 4px 10px rgba(255,255,255,0.08), 0 10px 16px rgba(0,0,0,0.28)",
          }}
        />
        <Box
          sx={{
            position: "absolute",
            top: { xs: 154, sm: 188 },
            left: "50%",
            width: { xs: 260, sm: 360 },
            height: { xs: 238, sm: 302 },
            transform: "translateX(-50%)",
            clipPath: "polygon(40% 0, 60% 0, 100% 100%, 0 100%)",
            background:
              "linear-gradient(180deg, rgba(255,232,164,0.64), rgba(255,226,145,0.24) 48%, rgba(255,226,145,0))",
            opacity: lampOn ? 0.76 : 0,
            transition: "opacity 260ms ease",
            animation: lampOn ? "scanLampWarm 2.8s ease-in-out infinite" : "none",
            mixBlendMode: "screen",
          }}
        />
        <ButtonBase
          aria-label={lampOn ? "Pull cord to turn login light off" : "Pull cord to turn login light on"}
          aria-pressed={lampOn}
          onPointerDown={startPull}
          onPointerMove={movePull}
          onPointerUp={endPull}
          onPointerCancel={endPull}
          onClick={handleCordClick}
          sx={{
            position: "absolute",
            top: { xs: 124, sm: 148 },
            right: { xs: 42, sm: 70 },
            width: 70,
            height: 170,
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "center",
            cursor: "grab",
            pointerEvents: "auto",
            touchAction: "none",
            "&:active": { cursor: "grabbing" },
          }}
        >
          <Box
            sx={{
              position: "relative",
              width: 32,
              height: 116 + pullDistance,
              transition: dragging ? "none" : "height 180ms ease",
            }}
          >
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: "50%",
                width: 2,
                height: "100%",
                transform: "translateX(-50%)",
                background: "repeating-linear-gradient(180deg, rgba(235,242,243,0.95) 0 5px, rgba(84,102,108,0.96) 5px 8px)",
                boxShadow: "0 8px 18px rgba(0,0,0,0.28)",
              }}
            />
            <Box
              sx={{
                position: "absolute",
                bottom: -19,
                left: "50%",
                width: 38,
                height: 38,
                transform: "translateX(-50%)",
                border: "3px solid rgba(228,236,238,0.94)",
                background: "radial-gradient(circle at 42% 34%, rgba(255,255,255,0.18), rgba(7,21,26,0.58) 58%)",
                boxShadow: lampOn
                  ? "0 0 0 6px rgba(255,226,145,0.12), inset 0 2px 4px rgba(255,255,255,0.2), 0 16px 28px rgba(0,0,0,0.28)"
                  : "inset 0 2px 4px rgba(255,255,255,0.16), 0 16px 28px rgba(0,0,0,0.28)",
                borderRadius: "50%",
              }}
            />
          </Box>
        </ButtonBase>
      </Box>

      <Box
        sx={{
          position: "relative",
          zIndex: 1,
          width: "min(100%, 1060px)",
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1fr 430px" },
          gap: { xs: 3, md: 6 },
          alignItems: "center",
          pt: { xs: 18, md: 8 },
        }}
      >
        <Stack
          spacing={3}
          sx={{
            display: { xs: "none", md: "flex" },
            color: "white",
            maxWidth: 500,
            opacity: lampOn ? 1 : 0.42,
            transform: lampOn ? "translateY(0)" : "translateY(10px)",
            transition: "opacity 260ms ease, transform 260ms ease",
          }}
        >
          <Box
            component="img"
            src="/slowlorislogoonlyt.png"
            alt="Slowloris"
            sx={{
              width: 126,
              height: 126,
              objectFit: "contain",
              filter: "drop-shadow(0 18px 32px rgba(0,0,0,0.38))",
            }}
          />
          <Box>
            <Typography
              sx={{
                fontSize: 14,
                fontWeight: 800,
                letterSpacing: 0,
                textTransform: "uppercase",
                color: "rgba(255,255,255,0.78)",
                mb: 1,
              }}
            >
              SCAN-APP
            </Typography>
            <Typography
              component="h1"
              sx={{
                fontSize: { md: 52, lg: 62 },
                lineHeight: 1,
                fontWeight: 900,
                letterSpacing: 0,
                textShadow: "0 18px 44px rgba(0,0,0,0.32)",
              }}
            >
              Slowloris operations
            </Typography>
          </Box>
        </Stack>

        <Paper
          elevation={0}
          sx={{
            width: "100%",
            p: { xs: 2.5, sm: 4 },
            borderRadius: 1,
            border: "1px solid rgba(255,255,255,0.42)",
            background: "rgba(255,255,255,0.86)",
            backdropFilter: "blur(18px)",
            boxShadow: "0 30px 90px rgba(0,0,0,0.34)",
            opacity: lampOn ? 1 : 0.2,
            filter: lampOn ? "none" : "blur(2px)",
            transform: lampOn ? "translateY(0) scale(1)" : "translateY(14px) scale(0.98)",
            pointerEvents: lampOn ? "auto" : "none",
            transition: "opacity 240ms ease, filter 240ms ease, transform 240ms ease",
          }}
        >
          <Stack spacing={3}>
            <Stack spacing={1.2}>
              <Box
                component="img"
                src="/slowlorislogoonlyt.png"
                alt="Slowloris"
                sx={{
                  display: { xs: "block", md: "none" },
                  width: 72,
                  height: 72,
                  objectFit: "contain",
                  mb: 0.5,
                }}
              />
              <Typography
                sx={{
                  fontSize: 12,
                  fontWeight: 900,
                  letterSpacing: 0,
                  textTransform: "uppercase",
                  color: "#006a71",
                }}
              >
                {eyebrow}
              </Typography>
              <Typography
                component="h2"
                sx={{
                  fontSize: { xs: 30, sm: 34 },
                  lineHeight: 1.08,
                  fontWeight: 900,
                  letterSpacing: 0,
                  color: "#0c1b22",
                }}
              >
                {title}
              </Typography>
              {subtitle && (
                <Typography sx={{ color: "#52636b", fontSize: 15, lineHeight: 1.55 }}>
                  {subtitle}
                </Typography>
              )}
            </Stack>

            {children}

            {footer && (
              <Box sx={{ pt: 0.5, borderTop: "1px solid rgba(12, 27, 34, 0.1)" }}>
                {footer}
              </Box>
            )}
          </Stack>
        </Paper>
      </Box>
    </Box>
  );
}
